import React from 'react';
import { Typography, Button, Grid, IconButton, Tooltip, Paper } from '@mui/material';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import UpdateIcon from '@mui/icons-material/Update';
import CloseIcon from '@mui/icons-material/Close';
import { convertTime } from '../../utils';
import ScheduleSelector from './ScheduleSelector';
import { EventDetail, PricingDetail } from '../../types';
import SelectChildModal from '../children/SelectChildModal';
import { PRICING_STATUS_ACTIVE } from '../../constants';
import { calculateDiscountedPrice } from '../../utils';
import LoginSignupModal from '../elements/LoginSignupModal';

interface EventPricingTiersBoxesProps {
  eventDetails: EventDetail;
  user: any;
  className: string;
  isItemInCart: (pricingId: number, childId: number) => boolean;
  isEventInCart: (eventId: number, childId: number) => boolean;
  handleAddToCartClick: (
    pricingId: number,
    isSelectScheduleAllowed: boolean,
    maxScheduleDays: number,
    selectedDaysArray: string[],
    handleAccordion: boolean,
  ) => void;
  handleRemoveItemFromCart: (pricingId: number) => void;
  handleAddToWaitlist: (pricingId: number) => void;
  expandedAccordions: Record<number, boolean>;
  setExpandedAccordions: React.Dispatch<React.SetStateAction<Record<number, boolean>>>;
}

const EventPricingTiersBoxes: React.FC<EventPricingTiersBoxesProps> = ({
  eventDetails,
  user,
  className,
  isItemInCart,
  isEventInCart,
  handleAddToCartClick,
  handleRemoveItemFromCart,
  handleAddToWaitlist,
  expandedAccordions,
  setExpandedAccordions,
}) => {
  const [showChildModal, setShowChildModal] = React.useState(false);
  const [showLoginSignupModal, setShowLoginSignupModal] = React.useState(false);

  return (
    <>
      <Typography variant="h6" gutterBottom pt={2} pb={2}>
        Pricing Tiers
      </Typography>
      <Grid container spacing={3}>
        {eventDetails.pricing
          .filter((pricing: PricingDetail) => pricing.status === PRICING_STATUS_ACTIVE)
          .map((pricing: PricingDetail) => (
            <Grid item xs={12} md={4} key={pricing.id}>
              <Paper elevation={3} sx={{ pt: 2, pb: 2, borderBottom: '0 !important' }}>
                <Grid container alignItems="center" spacing={2}>
                  <Grid item xs={12} sm={12} textAlign="center">
                    <Typography variant="h6" className="fw-700 ff-inter">
                      {pricing.description}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={12} textAlign="center">
                    <Typography variant="body2" className="ff-inter typography-time">
                      {convertTime(pricing.startTime)} - {convertTime(pricing.endTime)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={12} textAlign="center">
                    <Typography variant="body2" className="typography-price" color="#297AB0">
                      ${pricing.chargePerPeriod}
                    </Typography>
                  </Grid>
                  {pricing.discountType !== 0 && (
                    <Grid item xs={12} sm={12} textAlign="center">
                      <span className="item-pricing paid-full" style={{ color: '#483E92' }}>
                          ${calculateDiscountedPrice(
                        pricing.chargePerPeriod,
                        pricing.discountType,
                        pricing.discountAmount,
                        undefined,
                      )}{' '}
                        if Paid in Full
                        </span>
                    </Grid>
                  )}
                  {!isNaN(Number(pricing.depositAmount)) && Number(pricing.depositAmount) > 0 && (
                    <Grid item xs={12} sm={12} textAlign="center">
                      <span className="item-pricing paid-full" style={{ color: '#65892F' }}>
                        ${Number(pricing.depositAmount).toFixed(2)} Deposit
                      </span>
                    </Grid>
                  )}
                  <Grid item xs={12} sm={12} textAlign="center">
                    {!user && <Button onClick={() => setShowLoginSignupModal(true)}>Login or Sign Up</Button>}
                    {user && !user.selectedChild && (
                      <Button onClick={() => setShowChildModal(true)}>Select Child</Button>
                    )}
                    {user &&
                      user.selectedChild &&
                      className === 'registered' &&
                      <Typography paddingLeft={5} paddingRight={5} variant="body2" className="ftypography-price">
                        {user.selectedChild.fname} {user.selectedChild.lname} is registered for this event
                      </Typography>
                    }
                    {user &&
                      user.selectedChild &&
                      className === 'open' &&
                      eventDetails.occupiedSeats < eventDetails.capacity &&
                      (isItemInCart(pricing.id, user.selectedChild.id) ? (
                        <Button className="added-to-cart cursor-pointer btn-add-cart">
                          <Tooltip
                            title={expandedAccordions[pricing.id] ? 'Close' : 'Add to Cart'}
                            className="pt-0 btn-cart"
                          >
                            <IconButton className="ptf-0 plf-0 ptb-0">
                              <AddShoppingCartIcon />
                            </IconButton>
                          </Tooltip>
                          Added to Cart
                          <Tooltip
                            title="Remove item from cart"
                            className="pt-0 btn-cart"
                            onClick={() => handleRemoveItemFromCart(pricing.id)}
                          >
                            <IconButton className="ptf-0 ptb-0">
                              <CloseIcon />
                            </IconButton>
                          </Tooltip>
                        </Button>
                      ) : (
                        <Button
                          onClick={() =>
                            handleAddToCartClick(
                              pricing.id,
                              pricing.isSelectScheduleAllowed,
                              pricing.maxScheduleDays,
                              [],
                              true,
                            )
                          }
                          className={
                            isEventInCart(eventDetails.id, user.selectedChild.id)
                              ? 'cursor-pointer btn-add-cart grey-out'
                              : 'cursor-pointer btn-add-cart'
                          }
                        >
                          <Tooltip
                            title={expandedAccordions[pricing.id] ? 'Close' : 'Add to Cart'}
                            className="pt-0 btn-cart"
                          >
                            <IconButton className="ptf-0 plf-0 ptb-0">
                              {expandedAccordions[pricing.id] ? <CloseIcon /> : <AddShoppingCartIcon />}
                            </IconButton>
                          </Tooltip>
                          {expandedAccordions[pricing.id] ? 'Close' : 'Add to Cart'}
                        </Button>
                      ))}
                    {!!user &&
                      !!eventDetails.isWaitlistEnabled &&
                      eventDetails.occupiedSeats >= eventDetails.capacity && (
                        <Button onClick={() => handleAddToWaitlist(pricing.id)} className="cursor-pointer btn-add-cart">
                          <Tooltip title="Add to Waitlist" className="pt-0 btn-waitlist">
                            <IconButton className="ptf-0 ptb-0">
                              <UpdateIcon />
                            </IconButton>
                          </Tooltip>
                          Add to Waitlist
                        </Button>
                      )}
                  </Grid>
                </Grid>
                {pricing.isSelectScheduleAllowed && (
                  <ScheduleSelector
                    pricing={pricing}
                    expanded={expandedAccordions[pricing.id] || false}
                    setExpanded={(isExpanded) =>
                      setExpandedAccordions((prev) => ({ ...prev, [pricing.id]: isExpanded }))
                    }
                    onAddToCart={(selectedDays) =>
                      handleAddToCartClick(
                        pricing.id,
                        pricing.isSelectScheduleAllowed,
                        pricing.maxScheduleDays,
                        selectedDays,
                        false,
                      )
                    }
                  />
                )}
              </Paper>
            </Grid>
          ))}
      </Grid>
      <SelectChildModal open={showChildModal} onClose={() => setShowChildModal(false)} />
      <LoginSignupModal open={showLoginSignupModal} onClose={() => setShowLoginSignupModal(false)} />
    </>
  );
};

export default EventPricingTiersBoxes;
